import Autocomplete from '@trevoreyre/autocomplete-js';
import {PostcodeAutocompletionResult} from '../interfaces/postcode-autocompletion-result.interface';
import {UrlService} from '../services/url-service';

export class PostcodeSearchAutocomplete {
    static readonly MIN_SEARCH_LENGTH = 3;
    static readonly DEBOUNCE_TIME = 200;

    readonly autocompleteComponentId = 'postcode-typeahead';
    readonly autocompleteInputId = 'data-postcode-hero';

    private autocompleteComponent: HTMLDivElement;
    private autocompleteInput: HTMLInputElement;
    private autocompleteResults: HTMLUListElement;

    private autocompleteUrl: string;
    private autocompleteMapping: Record<string, string>;
    private searchTerm: string = undefined;
    private selectedResult: PostcodeAutocompletionResult = undefined;
    private searchResults: PostcodeAutocompletionResult[] = [];

    public init(): void {
        this.getElementsReferences();
        this.autocompleteUrl = this.getAutocompleteUrl();
        this.autocompleteMapping = this.getAutocompleteMapping();

        if (!this.autocompleteUrl) {
            throw new Error('Autocomplete URL is not defined, please set as "data-autocomplete-url" attribute on autocomplete element');
        }

        this.initAutocomplete(this.autocompleteComponentId);
    }

    private searchPostalCodes(input: string): Promise<PostcodeAutocompletionResult[]> {
        if (this.searchTerm !== input) {
            this.clearHiddenFields();
            this.searchResults = [];
        }

        if (input.length < PostcodeSearchAutocomplete.MIN_SEARCH_LENGTH) {
            this.selectedResult = undefined;
            this.searchResults = [];
        }

        this.searchTerm = input;

        const searchParams = UrlService.serializeObjectToQueryString({'query': input});
        const url = `${this.autocompleteUrl}?${searchParams}`

        return new Promise(resolve => {
            if (input.length < PostcodeSearchAutocomplete.MIN_SEARCH_LENGTH) {
                return resolve([])
            }

            void fetch(url)
                .then(response => response.json())
                .then((data: PostcodeAutocompletionResult[]) => {
                    this.searchResults = data;
                    resolve(data);
                })
        })
    }

    private initAutocomplete(id: string): void {
        new Autocomplete(`#${id}`, {
            search: this.searchPostalCodes.bind(this),
            debounceTime: PostcodeSearchAutocomplete.DEBOUNCE_TIME,
            renderResult: this.renderResult.bind(this),
            getResultValue: this.getResultValue.bind(this),
            onSubmit: this.onSubmit.bind(this),
        });
        this.autocompleteResults.classList.add('d-block');
        this.registerEvents();
    }

    private onSubmit(selectedResult: PostcodeAutocompletionResult): void {
        this.selectedResult = selectedResult;
        this.searchTerm = selectedResult.display;
        this.autocompleteInput.value = selectedResult.display;
        const data = this.mapResponseToCountryMapping(selectedResult, this.autocompleteMapping);
        this.setHiddenFields(data);
    }

    private getResultValue(selectedPostcodeResult: PostcodeAutocompletionResult): string {
        return selectedPostcodeResult.display;
    }

    private renderResult(result: PostcodeAutocompletionResult, props: string): string {
        return `<li class="dropdown-item" title="${result.display}" ${props}>
            ${result.display}
        </li>`;
    }

    private getAutocompleteUrl(): string {
        return this.autocompleteComponent.getAttribute('data-autocomplete-url');
    }

    private setArrowKeyNavigation(): void {
        this.autocompleteInput.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                this.autocompleteResults.querySelectorAll('li').forEach((li) => {
                    if (li.hasAttribute('aria-selected')) {
                        li.classList.add('active');
                    } else {
                        li.classList.remove('active');
                    }
                });
            }
        });
    }

    private getElementsReferences(): void {
        if (!this.autocompleteComponent) {
            this.autocompleteComponent = document.querySelector(`#${this.autocompleteComponentId}`);
        }
        if (!this.autocompleteInput) {
            this.autocompleteInput = this.autocompleteComponent.querySelector(`#${this.autocompleteInputId}`);
        }
        if (!this.autocompleteResults) {
            this.autocompleteResults = this.autocompleteComponent.querySelector('ul');
        }
    }

    private getAutocompleteMapping(): Record<string, string> {
        const mapping = this.autocompleteComponent.getAttribute('data-autocomplete-mapping');
        if (!mapping) {
            throw new Error('Autocomplete mapping is not defined, please set as ' +
                '"data-autocomplete-mapping" attribute on autocomplete element');
        }

        return JSON.parse(mapping);
    }

    private setHiddenFields(data: Record<string, string>): void {
        for (const key in data) {
            const value = data[key];
            const input: HTMLInputElement = document.querySelector(`input[name="${key}"]`);
            input.value = value;
        }
    }

    private mapResponseToCountryMapping(result: PostcodeAutocompletionResult, mapping: Record<string, string>): Record<string, string> {
        const region = JSON.parse(result.region);
        let data = {};
        for (const key in region) {
            if (key in mapping) {
                const regionItem = region[key];
                const dataKey = mapping[key];

                data[dataKey] = regionItem;
            }
        }
        return data;
    }

    private clearHiddenFields(): void {
        const hiddenFields = document.querySelectorAll('input[data-autocomplete-clearable="true"]');
        hiddenFields.forEach((input: HTMLInputElement) => {
            input.value = '';
        });
    }

    private registerEvents(): void {
        this.setArrowKeyNavigation();
        this.setBlurEvent();
        this.setEnterEvent();
    }

    private setBlurEvent(): void {
        this.autocompleteInput.addEventListener('blur', () => {
            this.handleMissingOptionCase();
        });
    }

    private setEnterEvent(): void {
        this.autocompleteInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                const selectedResult = this.searchResults.find((result) => {
                    return result.display === this.autocompleteInput.value;
                });

                if (selectedResult) {
                    this.onSubmit(selectedResult);
                } else {
                    this.handleMissingOptionCase();
                }
            }
        });
    }
    private handleMissingOptionCase(): void {
        if (!this.selectedResult) {
            this.autocompleteInput.value = '';
        } else {
            this.autocompleteInput.value = this.selectedResult.display;
        }
    }
}

const postcodeSearchAutocomplete = new PostcodeSearchAutocomplete();
window.addEventListener('load', () => {
    postcodeSearchAutocomplete.init();
});
